import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Loader2, RefreshCw, LogOut, ArrowLeft, Phone, MapPin, Navigation, CalendarClock, Clock, Route, MessageCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { fetchDemandes, deleteDemande } from "@/lib/api";

const STATUS_LABELS = {
  confirmee: "Demande reçue",
  assigne: "Chauffeur assigné",
  en_route: "En route",
  terminee: "Terminée",
};

const inputCls = "w-full bg-white border border-[#E6E1D8] px-4 py-3.5 text-sm text-obsidian placeholder:text-neutral-400 focus:outline-none focus:border-gold transition-colors duration-300";

const SITE_URL = process.env.REACT_APP_BACKEND_URL;

const toIntlPhone = (phone) => {
  const digits = (phone || "").replace(/[^0-9+]/g, "");
  if (digits.startsWith("+")) return digits.slice(1);
  if (digits.startsWith("00")) return digits.slice(2);
  if (digits.startsWith("0")) return "33" + digits.slice(1);
  return digits;
};

const whatsappShareUrl = (d) => {
  const link = `${SITE_URL}/suivi?code=${d.code}`;
  const dateStr = new Date(d.pickup_datetime).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" });
  const text = `Bonjour ${d.name}, votre réservation Chauffeur Privé DCR est confirmée (${d.code}) — ${dateStr}, ${d.price.toFixed(2)} €. Suivez votre course ici : ${link}`;
  return `https://wa.me/${toIntlPhone(d.phone)}?text=${encodeURIComponent(text)}`;
};

export default function Demandes() {
  const [pin, setPin] = useState(sessionStorage.getItem("dcr_pin") || "");
  const [unlocked, setUnlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demandes, setDemandes] = useState([]);

  const load = useCallback(async (value) => {
    const p = (value ?? pin).trim();
    if (!p) return;
    setLoading(true);
    try {
      const data = await fetchDemandes(p);
      setDemandes(data);
      setUnlocked(true);
      sessionStorage.setItem("dcr_pin", p);
    } catch (err) {
      if (!unlocked) toast.error(err.response?.data?.detail || "Code incorrect.");
    } finally {
      setLoading(false);
    }
  }, [pin, unlocked]);

  useEffect(() => {
    if (pin) load(pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!unlocked) return;
    const t = setInterval(() => load(), 30000);
    return () => clearInterval(t);
  }, [unlocked, load]);

  const lock = () => {
    sessionStorage.removeItem("dcr_pin");
    setPin("");
    setUnlocked(false);
    setDemandes([]);
  };

  const remove = async (code) => {
    if (!window.confirm(`Supprimer la réservation ${code} ?`)) return;
    try {
      await deleteDemande(code, pin);
      setDemandes((prev) => prev.filter((d) => d.code !== code));
      toast.success(`${code} supprimée.`);
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  return (
    <main className="min-h-screen flex flex-col" data-testid="demandes-page">
      <Nav />
      <section className="flex-1 pt-40 pb-24 px-6 sm:px-12 lg:px-20 max-w-5xl mx-auto w-full">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.215, 0.61, 0.355, 1] }}>
          <Link to="/" data-testid="demandes-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-gold transition-colors mb-10">
            <ArrowLeft size={13} /> Retour au site
          </Link>
          <p className="text-xs uppercase tracking-[0.25em] font-mono text-amber-700/80 font-semibold mb-6">Espace chauffeur</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] mb-12">
            Mes <span className="italic text-gold">demandes.</span>
          </h1>

          {!unlocked ? (
            <div className="max-w-md">
              <label className="block text-xs uppercase tracking-[0.2em] font-medium text-neutral-500 mb-2" htmlFor="pin">Code PIN</label>
              <div className="flex gap-4">
                <input
                  id="pin"
                  data-testid="demandes-pin-input"
                  type="password"
                  inputMode="numeric"
                  className={inputCls + " font-mono tracking-[0.3em]"}
                  placeholder="••••••"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load()}
                />
                <button
                  data-testid="demandes-unlock-button"
                  onClick={() => load()}
                  disabled={loading}
                  className="bg-obsidian text-cream text-xs uppercase tracking-[0.2em] px-7 py-3.5 hover:bg-gold hover:text-obsidian transition-colors duration-300 disabled:opacity-50 flex items-center gap-2 shrink-0"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />} Ouvrir
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-8">
                <p className="text-sm text-neutral-600" data-testid="demandes-count">{demandes.length} demande{demandes.length > 1 ? "s" : ""}</p>
                <div className="flex gap-3">
                  <button data-testid="demandes-refresh-button" onClick={() => load()} disabled={loading} className="flex items-center gap-2 border border-obsidian text-obsidian text-xs uppercase tracking-[0.2em] px-4 py-2.5 hover:bg-obsidian hover:text-cream transition-colors duration-300 disabled:opacity-50">
                    <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Actualiser
                  </button>
                  <button data-testid="demandes-lock-button" onClick={lock} className="flex items-center gap-2 border border-[#E6E1D8] text-neutral-500 text-xs uppercase tracking-[0.2em] px-4 py-2.5 hover:border-red-300 hover:text-red-700 transition-colors duration-300">
                    <LogOut size={13} /> Verrouiller
                  </button>
                </div>
              </div>

              <AnimatePresence>
                {demandes.length === 0 && (
                  <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm text-neutral-500 border border-[#E6E1D8] bg-white px-6 py-10 text-center" data-testid="demandes-empty">
                    Aucune demande pour le moment.
                  </motion.p>
                )}
                <div className="space-y-5">
                  {demandes.map((d) => (
                    <motion.article
                      key={d.code}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, ease: [0.215, 0.61, 0.355, 1] }}
                      className="bg-white border border-[#E6E1D8] p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6"
                      data-testid={`demande-card-${d.code}`}
                    >
                      <div className="lg:col-span-3">
                        <p className="font-mono text-sm tracking-[0.15em] text-obsidian mb-2">{d.code}</p>
                        <span className="inline-block text-xs uppercase tracking-[0.15em] bg-gold/10 text-amber-700 border border-gold/40 px-3 py-1.5" data-testid={`demande-status-${d.code}`}>
                          {STATUS_LABELS[d.status] || d.status}
                        </span>
                      </div>
                      <div className="lg:col-span-5 text-sm space-y-2.5">
                        <p className="font-medium text-obsidian">{d.name} · <a href={`tel:${d.phone}`} className="text-gold hover:underline inline-flex items-center gap-1" data-testid={`demande-phone-${d.code}`}><Phone size={12} />{d.phone}</a></p>
                        <p className="text-neutral-600 flex items-start gap-2">
                          {d.trip_type === "disposition" ? <Clock size={14} className="text-gold shrink-0 mt-0.5" /> : <Route size={14} className="text-gold shrink-0 mt-0.5" />}
                          {d.trip_type === "disposition" ? `Mise à disposition ${d.hours} h` : `Course ${d.category === "long" ? "longue distance" : "courte distance"} · ${d.distance_km} km`}
                        </p>
                        <p className="text-neutral-600 flex items-start gap-2"><MapPin size={14} className="text-gold shrink-0 mt-0.5" />{d.pickup}{d.dropoff ? <span className="inline-flex items-center gap-2"><Navigation size={12} className="text-neutral-400" />{d.dropoff}</span> : null}</p>
                        <p className="text-neutral-600 flex items-center gap-2"><CalendarClock size={14} className="text-gold shrink-0" />{new Date(d.pickup_datetime).toLocaleString("fr-FR", { dateStyle: "full", timeStyle: "short" })} · {d.passengers} pass. · {d.luggage} bag.</p>
                        {d.notes && <p className="text-xs text-neutral-500 italic">« {d.notes} »</p>}
                      </div>
                      <div className="lg:col-span-4 flex lg:flex-col justify-between lg:items-end gap-4">
                        <p className="font-serif text-3xl text-gold" data-testid={`demande-price-${d.code}`}>{d.price.toFixed(2)} €</p>
                        <a
                          href={whatsappShareUrl(d)}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`demande-whatsapp-${d.code}`}
                          className="inline-flex items-center gap-2 border border-obsidian text-obsidian text-xs uppercase tracking-[0.15em] px-4 py-2.5 hover:bg-[#25D366] hover:border-[#25D366] hover:text-white transition-colors duration-300"
                        >
                          <MessageCircle size={13} /> Envoyer le suivi
                        </a>
                        <button
                          data-testid={`demande-delete-${d.code}`}
                          onClick={() => remove(d.code)}
                          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-neutral-400 hover:text-red-600 transition-colors duration-300"
                        >
                          <Trash2 size={13} /> Supprimer
                        </button>
                        <p className="text-xs text-neutral-400">Reçue le {new Date(d.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</p>
                      </div>
                    </motion.article>
                  ))}
                </div>
              </AnimatePresence>
            </>
          )}
        </motion.div>
      </section>
    </main>
  );
}
